import { ui } from '@beelday/common'
import { UserRole } from 'common/types'
import { FunctionComponent, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useRoomProfiles } from 'room/common/use-room-profiles'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import { useSessionMode } from 'session-mode/use-session-mode'
import { assertUnreachable } from 'utils/unreachable'
import {
	clearVcrRoomAddress,
	setVcrRoomAddress,
} from 'video-conference-media/redux'
import { RoomTrainee } from './room-trainee'
import { RoomTraineeInPerson } from './room-trainee-in-person'
import { RoomTraineeInPersonMobile } from './room-trainee-in-person.mobile'
import { RoomTrainer } from './room-trainer'
import { RoomTrainerInPerson } from './room-trainer-in-person'
import { TooSmallForInPerson } from './too-small-for-in-person'

/**
 * The only place that knows the room matrix: mode x role x viewport. Keeping
 * it here is what lets `room-trainer.tsx` and `room-trainee.tsx` - the
 * highest-traffic screens in the product - stay exactly as they are.
 */
export const TrainingRoom: FunctionComponent = () => {
	const workflowUser = useAssertedWorkflowUser()
	const joinedRoomAddress = useAssertedJoinedRoomAddress()
	const dispatch = useDispatch()
	const mode = useSessionMode()
	const isDesktop = ui.responsive.useIsDesktop1024()
	useRoomProfiles()

	useEffect(() => {
		if (!joinedRoomAddress) {
			return
		}
		dispatch(setVcrRoomAddress(joinedRoomAddress))

		return () => {
			dispatch(clearVcrRoomAddress())
		}
	}, [dispatch, joinedRoomAddress])

	// The room's features have not arrived yet. Guessing here means rendering
	// the wrong shell for a frame, and in-person that means briefly showing a
	// video room to people who have no cameras.
	if (!mode) {
		return <ui.FullScreenSpinner />
	}

	if (mode === 'IN_PERSON') {
		// Below 1024px participants get the phone frame - tablets included, the
		// layout is fluid. The trainer drives a projector and is refused (D11).
		if (!isDesktop) {
			return workflowUser.role === UserRole.UPPER_ECHELON ? (
				<TooSmallForInPerson />
			) : (
				<RoomTraineeInPersonMobile />
			)
		}

		return workflowUser.role === UserRole.UPPER_ECHELON ? (
			<RoomTrainerInPerson />
		) : (
			<RoomTraineeInPerson />
		)
	}

	switch (workflowUser.role) {
		case UserRole.UPPER_ECHELON:
			return <RoomTrainer />
		case UserRole.END_USER:
			return <RoomTrainee />
		default:
			assertUnreachable('UserRole', workflowUser.role)
	}
}
